import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Users,
  Banknote,
  Settings,
  Search,
  CheckCircle,
  XCircle,
  Eye,
  Percent,
  RefreshCcw,
  ArrowRight,
  PlusCircle,
  Key,
  Loader2,
} from "lucide-react";
import axios from "@/utils/axios";
import { formatCurrency } from "@/utils/format";
import { toast } from "sonner";
import Loader from "@/components/loader/loader";

export default function AdminReferralManagement() {
  const [referrals, setReferrals] = useState([]);
  const [users, setUsers] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [referralPercent, setReferralPercent] = useState("");
  const [minWithdrawal, setMinWithdrawal] = useState("");
  const [maxWithdrawal, setMaxWithdrawal] = useState("");
  const [updating, setUpdating] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [generatingCodeUser, setGeneratingCodeUser] = useState(null);

  // Manual Credit Modal State
  const [creditModalOpen, setCreditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [creditAmount, setCreditAmount] = useState("");
  const [creditReason, setCreditReason] = useState("");
  const [crediting, setCrediting] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [referralsRes, withdrawalsRes, settingsRes, usersRes] =
        await Promise.all([
          axios.get("/admin/referral/referrals"),
          axios.get("/admin/referral/withdrawals"),
          axios.get("/admin/referral/settings"),
          axios.get("/admin/referral/users"),
        ]);

      if (referralsRes.data.success)
        setReferrals(referralsRes.data.data.referrals);
      if (withdrawalsRes.data.success)
        setWithdrawals(withdrawalsRes.data.data.withdrawals);
      if (settingsRes.data.success) {
        setReferralPercent(settingsRes.data.data.percentage.toString());
        setMinWithdrawal(settingsRes.data.data.minWithdrawal.toString());
        setMaxWithdrawal(settingsRes.data.data.maxWithdrawal.toString());
      }
      if (usersRes.data.success) setUsers(usersRes.data.data.users);
    } catch (error) {
      console.error("Error fetching admin referral data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleUpdateSettings = async () => {
    if (!referralPercent || isNaN(referralPercent))
      return toast.error("Invalid percentage");
    if (!minWithdrawal || isNaN(minWithdrawal))
      return toast.error("Invalid minimum withdrawal");
    if (!maxWithdrawal || isNaN(maxWithdrawal))
      return toast.error("Invalid maximum withdrawal");

    setUpdating(true);
    try {
      await axios.put("/admin/referral/settings", {
        percentage: parseFloat(referralPercent),
        minWithdrawal: parseFloat(minWithdrawal),
        maxWithdrawal: parseFloat(maxWithdrawal),
      });
      toast.success("Settings updated successfully");
    } catch (error) {
      toast.error("Failed to update settings");
    } finally {
      setUpdating(false);
    }
  };

  const handleWithdrawalAction = async (id, status) => {
    try {
      await axios.patch(`/admin/referral/withdrawals/${id}`, { status });
      toast.success(`Withdrawal ${status} successfully`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      await axios.patch(`/admin/referral/users/${userId}/status`, {
        enabled: !currentStatus,
      });
      toast.success(`Referral status updated successfully`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update user status");
    }
  };

  const handleGenerateCode = async (userId) => {
    setGeneratingCodeUser(userId);
    try {
      await axios.post(`/admin/referral/users/${userId}/generate-code`);
      toast.success("Referral code generated successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to generate referral code");
    } finally {
      setGeneratingCodeUser(null);
    }
  };

  const handleManualCredit = async () => {
    if (!selectedUser || !creditAmount || !creditReason) {
      return toast.error("Please fill all fields");
    }

    if (parseFloat(creditAmount) <= 0) {
      return toast.error("Amount must be greater than 0");
    }

    setCrediting(true);
    try {
      await axios.post("/admin/referral/users/credit", {
        userId: selectedUser.id,
        amount: parseFloat(creditAmount),
        reason: creditReason,
      });
      toast.success(
        `Successfully credited ${selectedUser.first_name} with ${formatCurrency(creditAmount)}`,
      );
      setCreditModalOpen(false);
      setCreditAmount("");
      setCreditReason("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to credit wallet");
    } finally {
      setCrediting(false);
    }
  };

  const getWithdrawalStatusColor = (status) => {
    switch (status) {
      case "paid":
        return "bg-green-100 text-green-700";
      case "approved":
        return "bg-blue-100 text-blue-700";
      case "rejected":
        return "bg-red-100 text-red-700";
      case "pending":
        return "bg-yellow-100 text-yellow-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-primary">
            Referral Management
          </h1>
          <p className="text-gray-600">
            Overview of all referrals and payout requests.
          </p>
        </div>
        <Button onClick={fetchData} variant="outline" size="icon">
          <RefreshCcw className="w-4 h-4" />
        </Button>
      </div>

      <div className="grid md:grid-cols-4 gap-6">
        <Card className="bg-primary/5 border-primary/10">
          <CardContent className="pt-6">
            <p className="text-sm text-gray-600 font-medium">Total Referrals</p>
            <h3 className="text-3xl font-bold text-primary mt-1">
              {referrals.length}
            </h3>
          </CardContent>
        </Card>
        <Card className="bg-yellow-50 border-yellow-100">
          <CardContent className="pt-6">
            <p className="text-sm text-yellow-800 font-medium">
              Pending Withdrawals
            </p>
            <h3 className="text-3xl font-bold text-yellow-600 mt-1">
              {withdrawals.filter((w) => w.status === "pending").length}
            </h3>
          </CardContent>
        </Card>
        <Card className="md:col-span-2">
          <CardHeader className="py-3 px-6">
            <CardTitle className="text-sm flex items-center gap-2">
              <Settings className="w-4 h-4" /> Global Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="flex-1">
                <Label className="text-[10px] uppercase font-bold text-gray-400">
                  Commission (%)
                </Label>
                <div className="relative">
                  <Percent className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="number"
                    placeholder="5"
                    className="pl-8"
                    value={referralPercent}
                    onChange={(e) => setReferralPercent(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex-1">
                <Label className="text-[10px] uppercase font-bold text-gray-400">
                  Min Withdrawal
                </Label>
                <Input
                  type="number"
                  placeholder="1000"
                  value={minWithdrawal}
                  onChange={(e) => setMinWithdrawal(e.target.value)}
                />
              </div>
              <div className="flex-1">
                <Label className="text-[10px] uppercase font-bold text-gray-400">
                  Max Withdrawal
                </Label>
                <Input
                  type="number"
                  placeholder="100000"
                  value={maxWithdrawal}
                  onChange={(e) => setMaxWithdrawal(e.target.value)}
                />
              </div>
            </div>
            <Button
              className="w-full bg-primary text-white"
              onClick={handleUpdateSettings}
              disabled={updating}
            >
              Update Settings
            </Button>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="withdrawals" className="w-full">
        <TabsList className="bg-gray-100 p-1 mb-4">
          <TabsTrigger
            value="withdrawals"
            className="data-[state=active]:bg-white"
          >
            <Banknote className="w-4 h-4 mr-2" /> Payout Requests
          </TabsTrigger>
          <TabsTrigger
            value="referrals"
            className="data-[state=active]:bg-white"
          >
            <Users className="w-4 h-4 mr-2" /> User Referrals
          </TabsTrigger>
          <TabsTrigger value="users" className="data-[state=active]:bg-white">
            <Users className="w-4 h-4 mr-2" /> Manage Users
          </TabsTrigger>
        </TabsList>

        <TabsContent value="withdrawals">
          <Card>
            <CardHeader>
              <CardTitle>Withdrawal Requests</CardTitle>
              <CardDescription>
                Review and process user payout requests.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
                    <tr>
                      <th className="px-4 py-3">User</th>
                      <th className="px-4 py-3">Amount</th>
                      <th className="px-4 py-3">Bank Details</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {withdrawals.map((w) => (
                      <tr
                        key={w.id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-4 py-4">
                          <p className="font-bold text-primary">
                            {w.users.first_name} {w.users.last_name}
                          </p>
                          <p className="text-xs text-gray-400">
                            {w.users.email}
                          </p>
                        </td>
                        <td className="px-4 py-4 font-black text-primary">
                          {formatCurrency(w.amount)}
                        </td>
                        <td className="px-4 py-4">
                          <p className="text-xs font-bold uppercase">
                            {w.bank_name}
                          </p>
                          <p className="text-xs font-mono">
                            {w.account_number}
                          </p>
                          <p className="text-[10px] text-gray-400">
                            {w.account_name}
                          </p>
                        </td>
                        <td className="px-4 py-4">
                          <Badge className={getWithdrawalStatusColor(w.status)}>
                            {w.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-4 text-right">
                          <div className="flex justify-end gap-2">
                            {w.status === "pending" && (
                              <>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-green-600 hover:bg-green-50"
                                  onClick={() =>
                                    handleWithdrawalAction(w.id, "approved")
                                  }
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-600 hover:bg-red-50"
                                  onClick={() =>
                                    handleWithdrawalAction(w.id, "rejected")
                                  }
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            {w.status === "approved" && (
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-white"
                                onClick={() =>
                                  handleWithdrawalAction(w.id, "paid")
                                }
                              >
                                Mark as Paid
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {withdrawals.length === 0 && (
                      <tr>
                        <td
                          colSpan="5"
                          className="text-center py-10 text-gray-400 italic"
                        >
                          No payout requests found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="referrals">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>All User Referrals</CardTitle>
                <CardDescription>
                  Track which users were referred by whom.
                </CardDescription>
              </div>
              <div className="relative w-64">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search email..."
                  className="pl-8"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
                    <tr>
                      <th className="px-4 py-3">Referred User</th>
                      <th className="px-4 py-3"></th>
                      <th className="px-4 py-3">Referrer</th>
                      <th className="px-4 py-3">Joined On</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {referrals
                      .filter((r) =>
                        r.email
                          .toLowerCase()
                          .includes(searchQuery.toLowerCase()),
                      )
                      .map((r) => (
                        <tr
                          key={r.id}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-4 py-4">
                            <p className="font-bold text-primary">
                              {r.first_name} {r.last_name}
                            </p>
                            <p className="text-xs text-gray-400">{r.email}</p>
                          </td>
                          <td className="px-4 py-4">
                            <ArrowRight className="w-4 h-4 text-gray-300" />
                          </td>
                          <td className="px-4 py-4">
                            <p className="font-bold text-primary">
                              {r.referred_by?.first_name}{" "}
                              {r.referred_by?.last_name}
                            </p>
                            <p className="text-xs text-gray-400">
                              {r.referred_by?.email}
                            </p>
                          </td>
                          <td className="px-4 py-4 text-xs text-gray-500">
                            {new Date(r.created_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="users">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>User Referral System Access</CardTitle>
                <CardDescription>
                  Enable or disable referral program for specific users.
                </CardDescription>
              </div>
              <div className="relative w-64">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search users..."
                  className="pl-8"
                  value={userSearchQuery}
                  onChange={(e) => setUserSearchQuery(e.target.value)}
                />
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
                    <tr>
                      <th className="px-4 py-3">User</th>
                      <th className="px-4 py-3">Referral Code</th>
                      <th className="px-4 py-3">Wallet Balance</th>
                      <th className="px-4 py-3">Referrals</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {users
                      .filter(
                        (u) =>
                          u.email
                            .toLowerCase()
                            .includes(userSearchQuery.toLowerCase()) ||
                          u.first_name
                            .toLowerCase()
                            .includes(userSearchQuery.toLowerCase()) ||
                          u.last_name
                            .toLowerCase()
                            .includes(userSearchQuery.toLowerCase()),
                      )
                      .map((u) => (
                        <tr
                          key={u.id}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-4 py-4">
                            <p className="font-bold text-primary">
                              {u.first_name} {u.last_name}
                            </p>
                            <p className="text-xs text-gray-400">{u.email}</p>
                          </td>
                          <td className="px-4 py-4 font-mono font-bold">
                            {u.referral_code && typeof u.referral_code === 'string' && u.referral_code !== 'null' && u.referral_code.trim() !== "" ? (
                              u.referral_code
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-[10px] px-2 py-0 border-primary text-primary hover:bg-primary/5"
                                onClick={() => handleGenerateCode(u.id)}
                                disabled={generatingCodeUser === u.id}
                              >
                                {generatingCodeUser === u.id ? (
                                  <Loader2 className="w-3 h-3 animate-spin mr-1" />
                                ) : (
                                  <Key className="w-3 h-3 mr-1" />
                                )}
                                Generate
                              </Button>
                            )}
                          </td>
                          <td className="px-4 py-4 font-black text-primary">
                            {formatCurrency(u.balance)}
                          </td>
                          <td className="px-4 py-4">
                            <Badge variant="outline">
                              {u._count.referrals} referrals
                            </Badge>
                          </td>
                          <td className="px-4 py-4">
                            <Badge
                              className={
                                u.referral_system_enabled
                                  ? "bg-green-100 text-green-700"
                                  : "bg-red-100 text-red-700"
                              }
                            >
                              {u.referral_system_enabled
                                ? "Enabled"
                                : "Disabled"}
                            </Badge>
                          </td>
                          <td className="px-4 py-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-primary text-primary hover:bg-primary/10"
                                onClick={() => {
                                  setSelectedUser(u);
                                  setCreditModalOpen(true);
                                }}
                              >
                                <PlusCircle className="w-4 h-4 mr-1" /> Credit
                              </Button>
                              <Button
                                size="sm"
                                variant={
                                  u.referral_system_enabled
                                    ? "destructive"
                                    : "default"
                                }
                                onClick={() =>
                                  handleToggleUserStatus(
                                    u.id,
                                    u.referral_system_enabled,
                                  )
                                }
                              >
                                {u.referral_system_enabled
                                  ? "Disable"
                                  : "Enable"}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={creditModalOpen} onOpenChange={setCreditModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manual Referral Credit</DialogTitle>
            <DialogDescription>
              Add funds manually to {selectedUser?.first_name}'s referral
              wallet. This action will be logged.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="credit-amount">Amount</Label>
              <Input
                id="credit-amount"
                type="number"
                placeholder="0.00"
                value={creditAmount}
                onChange={(e) => setCreditAmount(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="credit-reason">Reason (Compulsory)</Label>
              <Textarea
                id="credit-reason"
                placeholder="Why are you adding these funds?"
                value={creditReason}
                onChange={(e) => setCreditReason(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreditModalOpen(false)}>
              Cancel
            </Button>
            <Button
              className="bg-primary text-white"
              onClick={handleManualCredit}
              disabled={crediting || !creditAmount || !creditReason}
            >
              {crediting ? "Processing..." : "Add Funds"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
